import { api, type QueryParams, type RawResult } from '@/lib/api/client';
import type {
  ContactSubmission,
  JobApplication,
  LeadStatus,
  NewsletterSubscriber,
  Paginated,
  QuoteRequest,
} from '@/lib/api/types';

/**
 * Lead type union — the four lead types share one engine (spec §11 / config/leadTypes.ts).
 */
export type LeadTypeKey = 'contact' | 'quotes' | 'applications' | 'newsletter';

/** A lead record — one of the four entity shapes. The engine renders them generically. */
export type Lead = ContactSubmission | QuoteRequest | JobApplication | NewsletterSubscriber;

/** PATCH body for the manage panel. Only provided fields are updated; notes REPLACES. */
export interface LeadUpdate {
  status?: LeadStatus;
  assigned_to?: string | null;
  notes?: string;
}

/**
 * Lead engine API (spec §11, docs/api/admin/leads + management.md). One client parameterized
 * by type; the backend exposes the same shape for all four under `/admin/leads/{type}`.
 */
export const leadsApi = {
  /** Paginated list with the shared filter set (status, assigned_to, q, from, to). */
  async list(type: LeadTypeKey, params?: QueryParams): Promise<Paginated<Lead>> {
    const res = await api.getRaw<Lead[]>(`/admin/leads/${type}`, params);
    return { data: res.data ?? [], meta: res.meta as Paginated<Lead>['meta'] };
  },

  /** Single record by id (contact/quotes/applications have detail endpoints). */
  get(type: LeadTypeKey, id: string): Promise<Lead> {
    return api.get<Lead>(`/admin/leads/${type}/${id}`);
  },

  /** Update status / assigned_to / notes. Partial PATCH; requires `leads:manage`. */
  update(type: LeadTypeKey, id: string, body: LeadUpdate): Promise<Lead> {
    return api.patch<Lead>(`/admin/leads/${type}/${id}`, body);
  },

  /** PDPL hard delete (action `purge`) — permanent PII removal. Requires `leads:manage`. */
  erase(type: LeadTypeKey, id: string): Promise<null> {
    return api.del<null>(`/admin/leads/${type}/${id}`);
  },

  /**
   * CSV export honoring the active filters. Returns the RawResult so the caller can read
   * `headers['X-Export-Truncated']` and trigger a blob download. Capped at 5,000 rows.
   */
  exportCsv(type: LeadTypeKey, params?: QueryParams): Promise<RawResult<Blob>> {
    return api.download(`/admin/leads/${type}/export`, params);
  },
};
